import { Injectable } from '@angular/core';
import { UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import {
  ExistingNamesDirective,
  ForbiddenNamesDirective,
  FullNameDirective,
  ValidCompanyNameDirective,
  ValidEmailDirective,
  ValidNameDirective,
  MatchBirthdateDirective,
  MatchCpfDirective,
  MatchNameDirective,
  ValidCpfDirective,
  ValidCnpjDirective,
  ValidPhoneDirective,
  ValidEmailPhoneDirective,
  SpecialPatternDirective,
  MatchRegexDirective,
  ValidDateDirective,
  NotOnlyNumbersDirective,
  SecureUrlDirective
} from '@app/shared/validators';
import { ValidationKey } from '@app/models';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {
  private frontendValidations: { [key in ValidationKey]: [string, any, any?] } = {
    required: ['error_field_required', Validators.required],
    email: ['error_must_be_a_valid_email_address', Validators.email],
    minlength: ['error_must_be_at_least', Validators.minLength, { min: 'requiredLength' }],
    maxlength: ['error_may_not_be_greater_than', Validators.maxLength, { max: 'requiredLength' }],
    pattern: ['error_format_is_invalid', Validators.pattern],
    mask: ['error_format_is_invalid', () => {}],
    appFullName: ['error_incomplete_name', FullNameDirective.validator],
    appValidName: ['error_invalid_name', ValidNameDirective.validator],
    appValidCompanyName: ['error_invalid_company_name', ValidCompanyNameDirective.validator],
    appValidEmail: ['error_invalid_email', ValidEmailDirective.validator],
    appValidCpf: ['error_invalid_cpf', ValidCpfDirective.validator],
    appValidCnpj: ['error_invalid_cnpj', ValidCnpjDirective.validator],
    appValidPhone: ['error_invalid_phone_number', ValidPhoneDirective.validator],
    appValidEmailPhone: ['error_invalid_email_phone_number', ValidEmailPhoneDirective.validator],
    appForbiddenNames: ['error_invalid_name', ForbiddenNamesDirective.validator],
    appExistingNames: ['error_existing_name', ExistingNamesDirective.validator],
    appMatchBirthdate: ['error_incorrect_birthdate', MatchBirthdateDirective.validator],
    appMatchCpf: ['error_incorrect_cpf', MatchCpfDirective.validator],
    appMatchName: ['error_incorrect_name', MatchNameDirective.validator],
    appSpecialPattern: ['error_format_has_special_chars', SpecialPatternDirective.validator],
    appMatchRegex: ['error_regex_not_matched', MatchRegexDirective.validator],
    appValidDate: ['error_invalid_date_format', ValidDateDirective.validator, { format: 'format' }],
    appNotOnlyNumbers: ['error_not_only_numbers', NotOnlyNumbersDirective.validator],
    appSecureUrl: ['error_secure_url', SecureUrlDirective.validator]
  };

  constructor(private translateService: TranslateService) {}

  validate(value: any, validations: ValidationKey[], validationsParams?: { [attr: string]: any[] }): ValidationErrors | null {
    validationsParams = validationsParams || {};
    if (validations.length === 0) {
      return null;
    }

    const control = new UntypedFormControl(
      value,
      validations
        .map(validation =>
          validationsParams[validation] && this.frontendValidations[validation]
            ? this.frontendValidations[validation][1].bind(null, ...[new UntypedFormControl(value)].concat(validationsParams[validation] || []))
            : this.frontendValidations[validation]
              ? this.frontendValidations[validation][1]
              : undefined
        )
        .filter(validation => !!validation)
    );
    return Object.keys(control.errors || {}).length > 0 ? control.errors : null;
  }

  getErrorMessage(error: ValidationErrors | string) {
    const validation = (Object.keys(this.frontendValidations) as ValidationKey[]).find(key => !!error && (typeof error === 'string' ? error === key : !!error[key]));

    const params: { [key: string]: any } = {};
    if (typeof error !== 'string' && validation && this.frontendValidations[validation] && this.frontendValidations[validation][2]) {
      const paramsMap = this.frontendValidations[validation][2];
      const errorValue = error[validation];
      Object.keys(paramsMap).forEach(key => {
        let value = errorValue[paramsMap[key]];
        if (typeof value === 'string' && value.startsWith('date_format.')) {
          value = this.translateService.instant(value);
        }
        params[key] = value;
      });
    }
    return validation ? this.translateService.instant(this.frontendValidations[validation][0], params) : error && error !== 'string' ? (error as ValidationErrors).server : error;
  }
}
